import { Toggle, Translation } from '@ps/web-shared-components';
import type { ChangeEvent } from 'react';
import React, { useContext, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Context as NavigationContext } from '@swf/components/StarsWizard/components/Navigation/Navigation';
import useWizardContext from '@swf/core/hooks/useWizardContext';
import type { Dispatch } from '@swf/core/store';

import CTAButton from '../../../CTAButton/CTAButton';
import Container from '../../../Container/Container';
import RMFExtraVerificationTransition from '../RMFExtraVerificationTransition/RMFExtraVerificationTransition';

import {
  TXTCLI_Almost_thereX_you_just_need_to_ELL,
  TXTCLI_Knowledge_based_authentication,
  TXTCLI_Please_select_an_answerX,
  TXTCLI_Your_knowledge_based_authentic_ELL
} from './RMFKBA.messages.json';
import { errorMessage } from './RMFKBA.scss';

const RMFKBA: React.FC = () => {
  const {
    data: { kbaQuestions, kbaAnswers, kbaQuestionsPointer }
  } = useWizardContext();

  const {
    account: { updateAccount }
  } = useDispatch<Dispatch>();

  const { goPrevious, goNext } = useContext(NavigationContext);

  const [error, setError] = useState<boolean>(false);

  const additionalQuestionExists = kbaQuestionsPointer.current === 3;

  const [showExtraVerification, setShowExtraVerification] = useState(false);
  const [questionStep, setQuestionStep] = useState({
    current: kbaQuestions?.[kbaQuestionsPointer?.current],
    next: kbaQuestions?.[kbaQuestionsPointer?.next],
    prev: kbaQuestions?.[kbaQuestionsPointer?.prev]
  });

  const updateQuestionStep = () => {
    setQuestionStep(() => ({
      current: kbaQuestions?.[kbaQuestionsPointer.current],
      next: kbaQuestions?.[kbaQuestionsPointer.next],
      prev: kbaQuestions?.[kbaQuestionsPointer.prev]
    }));
  };

  const handleNextTick = () => {
    kbaQuestionsPointer.current = kbaQuestionsPointer.next;
    kbaQuestionsPointer.next = kbaQuestionsPointer.current + 1;
    kbaQuestionsPointer.prev = kbaQuestionsPointer.current - 1;
    updateQuestionStep();
  };

  const onClickHandler = () => {
    const answerSelected = kbaAnswers?.[kbaQuestionsPointer?.current];
    if (!answerSelected) {
      setError(true);
      return;
    }
    if (questionStep.next) {
      handleNextTick();
    } else if (kbaQuestions?.length === 3) {
      updateAccount({
        kbaAnswers,
        realMoneyFlag: true
      })
        .then((response) => {
          if (response.success && kbaQuestions && response.kbaQuestions?.length) {
            kbaQuestions.push(...response.kbaQuestions);
            handleNextTick();
          } else if (response.success && !response.kbaQuestions?.length) {
            goNext();
          } else if (!response.success) {
            setShowExtraVerification(true);
          }
        })
        .finally(() => {
          updateQuestionStep();
        });
    } else if (kbaAnswers) {
      updateAccount({
        kbaAnswers: [kbaAnswers[kbaAnswers.length - 1]],
        realMoneyFlag: true
      })
        .then((response) => {
          if (response.success) {
            goNext();
          } else {
            setShowExtraVerification(true);
          }
        })
        .finally(() => {
          updateQuestionStep();
        });
    }
  };

  const onChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
    setError(false);
    if (kbaAnswers && questionStep.current) {
      kbaAnswers[kbaQuestionsPointer.current] = {
        questionID: questionStep.current.questionID,
        answer: e.target.value
      };
    }
  };

  const onBackClick = () => {
    if (questionStep.prev) {
      kbaQuestionsPointer.current = kbaQuestionsPointer.prev;
      kbaQuestionsPointer.next = kbaQuestionsPointer.current + 1;
      kbaQuestionsPointer.prev = kbaQuestionsPointer.current - 1;
      updateQuestionStep();
    } else {
      goPrevious();
    }
  };

  return (
    <Container
      page={RMFKBA}
      headerProps={{
        title: <Translation {...TXTCLI_Your_knowledge_based_authentic_ELL} />,
        buttons: {
          onBackClick,
          back: !additionalQuestionExists,
          allowMultiClick: true,
          hideCloseButton: !additionalQuestionExists
        }
      }}
      pageTopTitle={<Translation {...TXTCLI_Knowledge_based_authentication} />}
      pageTopSubtitle={<Translation {...TXTCLI_Almost_thereX_you_just_need_to_ELL} />}
    >
      {questionStep.current && (
        <div>
          <h4>{questionStep.current.question}</h4>
          {questionStep.current.choices.map((choice, index) => (
            <Toggle
              key={index}
              id={choice}
              name={`answer${kbaQuestionsPointer.current}`}
              label={choice}
              type="radio"
              value={choice}
              onChange={onChangeHandler}
              error={error}
              validationErrors={[{ valid: error, message: TXTCLI_Knowledge_based_authentication }]}
            />
          ))}
          {error && (
            <div className={errorMessage}>
              <Translation {...TXTCLI_Please_select_an_answerX} />
            </div>
          )}
        </div>
      )}
      {showExtraVerification && <RMFExtraVerificationTransition onClose={() => goNext()} />}
      <CTAButton type="button" onClick={onClickHandler} />
    </Container>
  );
};

RMFKBA.displayName = 'RMFKBA';

export default RMFKBA;
